import type { MeterKind } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import type { AssetRow } from './fleet.ts'
import type { WorkOrderPartRow } from './catalog.ts'
import type { WorkOrderRow } from './safety.ts'

/** The workshop's screen logic (M23-F), pure, so that the pages stay thin and the rules are proven here. */
type T = (k: string, o?: Record<string, unknown>) => string

/** "Toro GM-3000": the machine as the mechanic names it (M23 item 33: the workshop and the service list named machines by tag only). Nothing when the register holds neither make nor model. */
export const machineName = (a: Pick<AssetRow, 'make' | 'model'> | undefined | null): string => (a ? [a.make, a.model].filter(Boolean).join(' ') : '')

/** The products most recently used on machines of the same type as this one (M23 item 36), newest line first, each product once, at most `n`: read from the mirrored part
 *  lines joined to the orders' machines. A removed line still says the product was used. A machine with no type is matched with itself only. */
export function recentProductIds(parts: readonly Pick<WorkOrderPartRow, 'work_order_id' | 'product_id' | 'created_at'>[], orders: readonly Pick<WorkOrderRow, 'id' | 'asset_id'>[],
  assets: readonly Pick<AssetRow, 'id' | 'asset_type_code'>[], assetId: string, n = 3): string[] {
  const me = assets.find((a) => a.id === assetId)
  const type = me?.asset_type_code ?? null
  const sameType = new Set(assets.filter((a) => (type ? a.asset_type_code === type : a.id === assetId)).map((a) => a.id))
  const orderOk = new Set(orders.filter((w) => sameType.has(w.asset_id)).map((w) => w.id))
  const out: string[] = []
  for (const p of [...parts].filter((p) => orderOk.has(p.work_order_id)).sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))) {
    if (!out.includes(p.product_id)) out.push(p.product_id)
    if (out.length >= n) break
  }
  return out
}

/** A service recorded on a machine, as the device holds it (fleet.service_event is mirrored: M23 item 45 shows the last one offline). */
export interface ServiceEventRow { id: string; asset_id: string; plan_id: string; performed_at: string; performed_local_date: string; note?: string | null }
export const useServiceEvents = () => useMerged<ServiceEventRow>('service_event', 'fleet.service_event')
/** The newest service of a machine, or none. */
export function lastServiceEvent<E extends Pick<ServiceEventRow, 'asset_id' | 'performed_at'>>(events: readonly E[] | undefined, assetId: string): E | undefined {
  return (events ?? []).filter((e) => e.asset_id === assetId).sort((a, b) => (a.performed_at < b.performed_at ? 1 : -1))[0]
}

/** A duration as a screen prints it (M23 item 44, the order's downtime and labour): whole hours and minutes, "3 h 05" or "45 min"; never a decimal hour. */
export function durationParts(seconds: number): { h: number; m: number } {
  const s = Math.max(0, Math.round(seconds))
  return { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60) }
}
export function durationText(seconds: number, t: T): string {
  const { h, m } = durationParts(seconds)
  return h > 0 ? t('workshop.duration.hm', { h, m: String(m).padStart(2, '0') }) : t('workshop.duration.m', { m })
}

/** The meter kinds a machine has (M23 item 38: the plan form hides the counter-kind select when the machine has one kind). */
export const meterKindsOf = (slots: readonly { asset_id: string; kind: MeterKind }[] | undefined, assetId: string): MeterKind[] =>
  [...new Set((slots ?? []).filter((s) => s.asset_id === assetId).map((s) => s.kind))]

/** The ids of a machine page's sections, where the workshop's cards open it (M23 item 33: a TanStack `hash`). */
export const ASSET_SECTIONS = { safety: 'safety', workorders: 'workorders', meters: 'meters', service: 'service' } as const
